import { initialize } from 'redux-form';
import uuid from 'node-uuid';
import ref, { TIMESTAMP } from 'services/firebaseRef';
import { getValidUntilTime } from 'services/payment';
import {publishEvent, sendClassApprovedEvents} from "services/events";
import program from '../../programs/index';
import { withDefaultLanguageIfNotPresent } from 'helpers/i18n'

export const SESSION_CREATE = 'SESSION_CREATE';
export const SESSION_UPDATE = 'SESSION_UPDATE';
export const SESSION_BUY = 'SESSION_BUY';
export const SESSION_SET_CURRENT_SLIDE = 'SESSION_SET_CURRENT_SLIDE';
export const SESSION_SET_LAST_SLIDE = 'SESSION_SET_LAST_SLIDE';
export const SESSION_SET_VIDEO_STATE = 'SESSION_SET_VIDEO_STATE';
export const SESSION_SET_TEAM_NAME = 'SESSION_SET_TEAM_NAME';
export const SESSION_SET_SCORE = 'SESSION_SET_SCORE';
export const SESSION_SET_MISSION_REPORT_ANSWER =
  'SESSION_SET_MISSION_REPORT_ANSWER';
export const SESSION_SET_HACKED_ANSWER = 'SESSION_SET_HACKED_ANSWER';
export const SESSION_SET_MODE = 'SESSION_SET_MODE';
export const SESSION_SET_CURRENT_LEVEL = 'SESSION_SET_CURRENT_LEVEL';
export const SESSION_COMPLETE_LEVEL = 'SESSION_COMPLETE_LEVEL';
export const SESSION_SET_CONFIGURATION = 'SESSION_SET_CONFIGURATION';
export const SESSION_UNARCHIVE = 'SESSION_UNARCHIVE';
export const SESSION_ARCHIVE = 'SESSION_ARCHIVE';
export const NO_SESSIONS = 'NO_SESSIONS';

function getSessionById(sessionId) {
  return ref.child('sessions').child(sessionId)
    .once('value')
    .then(sessionRef => withDefaultLanguageIfNotPresent(sessionRef.val()));
}

function sessionCreate (sessionId, data) {
  return {
    type: SESSION_CREATE,
    sessionId,
    data: withDefaultLanguageIfNotPresent(data)
  };
}

export function createSession (userId, programId, params) {
  return () => {
    const {contractId, pendingApproval, referrerUserId} = params;
    const isApproved = !pendingApproval && contractId;
    const validUntil = getValidUntilTime();
    return new Promise((resolve, reject) => {
      const id = uuid.v4();
      const createdAt = TIMESTAMP;
      const updatedAt = TIMESTAMP;
      const {
        language,
        grade,
        divisionName,
        numberOfStudents,
        auto
      } = params;
      const configuration = 'dualScreen';
      const data = {
        userId,
        programId,
        createdAt,
        updatedAt,
        language,
        grade,
        divisionName,
        numberOfStudents,
        configuration,
        pendingApproval,
        auto: !!auto
      };
      if (isApproved) {
        data.funding = 'CONTRACT';
        data.staffUserId = 'auto';
        data.contractId = contractId;
        data.approvedAt = createdAt;
        data.validUntil = validUntil;
      }
      if (referrerUserId) {
        data.referrerUserId = referrerUserId;
      }
      ref.child('sessions').child(id).set(data, error => {
        if (error) {
          reject(error);
        } else {
          resolve(id);
        }
      });
    }).then(id => {
      return new Promise((resolve, reject) => {
        ref.child('users').child(userId).child('sessions')
        .push(id, error => {
          if (error) {
            reject(error);
          } else {
            resolve(id);
          }
        });
      });
    }).then(sessionId => {
      const {language, grade, divisionName, numberOfStudents, firstClass} = params;
      if (firstClass) {
        const eventData = {
          userId,
          sessionId,
          programId,
          language: language,
          grade,
          divisionName,
          numberOfStudents,
          referrerUserId
        };
        publishEvent('First Class Created', eventData);
      }
      return sessionId;
    }).then(sessionId => {
      const {language, grade, divisionName, numberOfStudents} = params;
      const eventData = {
        userId,
        sessionId,
        programId,
        language,
        grade,
        divisionName,
        numberOfStudents,
        referrerUserId,
        pendingApproval // GTM note: required for intercom
      };
      publishEvent('Class Created', eventData);
      if (isApproved) {
        sendClassApprovedEvents({
          userId,
          sessionId,
          programId,
          contractId,
          staffUserId: 'auto',
          validUntil,
          noEmail: false,
          language
        });
      }
      if (referrerUserId) {
        publishEvent('Class Referral Completed', {
          userId: referrerUserId,
          refereeUserId: userId,
          sessionId,
          programId,
          language
        })
      }
    }).catch(error => {
      throw error;
    });
  };
}

function sessionUpdate (sessionId, data) {
  return {
    type: SESSION_UPDATE,
    sessionId,
    data
  };
}

export function updateSession (sessionId, programId, data, userId) {
  return dispatch => {
    const updatedAt = TIMESTAMP;
    const updateData = {
      ...data,
      updatedAt
    };
    return new Promise((resolve, reject) => {
      ref.child('sessions').child(sessionId).update(updateData, error => {
        if (error) {
          reject(error);
        } else {
          resolve(sessionId);
        }
      });
    }).then(() => {
      return dispatch(
        sessionUpdate(sessionId, data)
      );
    }).then(() => {
      const {language, ...rest} = data;
      publishEvent(`Class Updated`, {
        userId,
        sessionId,
        programId,
        language,
        ...rest
      });
    }).catch(error => {
      throw error;
    });
  };
}

function sessionBuy(sessionId, data) {
  return {
    type: SESSION_BUY,
    sessionId,
    data
  };
}

export function buySession (sessionId, programId, data, userId) {
  return dispatch => {
    const updatedAt = TIMESTAMP;
    const buyData = {
      ...data,
      funding: 'STRIPE',
      validUntil: getValidUntilTime()
    };

    return ref
      .child('sessions')
      .child(sessionId)
      .update({
        ...buyData,
        updatedAt
      })
      .then(() => {
        dispatch(sessionBuy(sessionId, buyData));
        return getSessionById(sessionId);
      })
      .then(({language}) => {
        publishEvent('Class Purchased', {
          userId,
          sessionId,
          programId,
          funding: 'STRIPE',
          stripeChargeId: buyData.stripeChargeId,
          promoCode: buyData.promoCode,
          paidAmount: buyData.paidAmount,
          paidCurrency: buyData.paidCurrency,
          validUntil: buyData.validUntil,
          language
        }, ['validUntil']);
      });
  };
}

function fetchSession (dispatch, refs, sessionId) {
  const sessionRef = ref.child('sessions').child(sessionId);
  sessionRef.on('value', snapshot => {
    const value = snapshot.val();
    if (value) {
      dispatch(sessionCreate(sessionId, value));
    }
  });
  refs.push(sessionRef);
}

function noSessions (userId) {
  return {
    type: NO_SESSIONS,
    userId
  };
}

export function bindSessions (userId, refs) {
  return dispatch => {
    const userSessionRef = ref.child('users').child(userId).child('sessions');
    userSessionRef.once('value', snapshot => {
      if (snapshot && snapshot.val() === null) {
        dispatch(noSessions(userId));
      }
    });
    userSessionRef.on('child_added', snapshot => {
      const sessionId = snapshot.val();
      fetchSession(dispatch, refs, sessionId);
    });
    refs.push(userSessionRef);
  };
}

export function bindSingleSession (sessionId, refs) {
  return dispatch => {
    fetchSession(dispatch, refs, sessionId);
  };
}

export function unbind (refs) {
  return () => {
    refs.forEach(savedRef => {
      savedRef.off();
    });
  };
}

function sessionSetLastSlide (sessionId, lastSlide) {
  return {
    type: SESSION_SET_LAST_SLIDE,
    sessionId,
    lastSlide
  };
}

export function setLastSlide ({sessionId, lastSlide = null}) {
  return dispatch => {
    return new Promise((resolve, reject) => {
      ref.child('sessions').child(sessionId).update({
        lastSlide
      }, error => {
        if (error) {
          reject(error);
        } else {
          resolve();
        }
      });
    }).then(() => {
      return dispatch(
          sessionSetLastSlide(sessionId, lastSlide)
      );
    });
  };
}

function sessionSetCurrentSlide (sessionId, currentSlide) {
  return {
    type: SESSION_SET_CURRENT_SLIDE,
    sessionId,
    currentSlide
  };
}

export function setCurrentSlide ({sessionId, currentSlide = null}) {
  return dispatch => {
    return new Promise((resolve, reject) => {
      ref.child('sessions').child(sessionId).update({
        currentSlide,
        videoState: {}
      }, error => {
        if (error) {
          reject(error);
        } else {
          resolve();
        }
      });
    }).then(() => {
      return dispatch(
        sessionSetCurrentSlide(sessionId, currentSlide)
      );
    });
  };
}

function sessionSetVideoState (sessionId, videoState) {
  return {
    type: SESSION_SET_VIDEO_STATE,
    sessionId,
    videoState
  };
}

export function setVideoState ({sessionId, videoState}) {
  return dispatch => {
    return new Promise((resolve, reject) => {
      ref.child('sessions').child(sessionId).update({
        videoState
      }, error => {
        if (error) {
          reject(error);
        } else {
          resolve();
        }
      });
    }).then(() => {
      return dispatch(
        sessionSetVideoState(sessionId, videoState)
      );
    });
  };
}

function sessionSetTeamName (sessionId, team, teamName) {
  return {
    type: SESSION_SET_TEAM_NAME,
    sessionId,
    team,
    teamName
  };
}

export function setTeamName ({sessionId, team, teamName}) {
  return dispatch => {
    return new Promise((resolve, reject) => {
      ref.child('sessions').child(sessionId).child('teamNames').update({
        [team]: teamName
      }, error => {
        if (error) {
          reject(error);
        } else {
          resolve();
        }
      });
    }).then(data => {
      return new Promise((resolve, reject) => {
        ref.child('sessions').child(sessionId).update({
          updatedAt: TIMESTAMP
        }, error => {
          if (error) {
            reject(error);
          } else {
            resolve(data);
          }
        });
      });
    }).then(() => {
      return dispatch(
        sessionSetTeamName(sessionId, team, teamName)
      );
    });
  };
}

function sessionSetScore (sessionId, team, score) {
  return {
    type: SESSION_SET_SCORE,
    sessionId,
    team,
    score
  };
}

export function setScore ({sessionId, team, score}) {
  return dispatch => {
    return new Promise((resolve, reject) => {
      ref.child('sessions').child(sessionId).child('scores').update({
        [team]: score
      }, error => {
        if (error) {
          reject(error);
        } else {
          resolve();
        }
      });
    }).then(data => {
      return new Promise((resolve, reject) => {
        ref.child('sessions').child(sessionId).update({
          updatedAt: TIMESTAMP
        }, error => {
          if (error) {
            reject(error);
          } else {
            resolve(data);
          }
        });
      });
    }).then(() => {
      return dispatch(
        sessionSetScore(sessionId, team, score)
      );
    });
  };
}

function sessionSetMissionReportAnswer ({
  sessionId,
  level,
  questionNumber,
  question,
  answer,
  isValid
}) {
  return {
    type: SESSION_SET_MISSION_REPORT_ANSWER,
    sessionId,
    level,
    questionNumber,
    question,
    answer,
    isValid
  };
}

export function setMissionReportAnswer ({
    sessionId,
    level,
    questionNumber,
    question,
    answer,
    invalid
}) {
  const questionId = questionNumber ? `Q${questionNumber}` : 'hacked';
  return dispatch => {
    return new Promise((resolve, reject) => {
      ref.child('sessions').child(sessionId).child('missionReports')
      .child(`L${level}`).child(questionId).update({
        question,
        answer,
        invalid
      }, error => {
        if (error) {
          reject(error);
        } else {
          resolve();
        }
      });
    }).then(data => {
      return new Promise((resolve, reject) => {
        ref.child('sessions').child(sessionId).update({
          updatedAt: TIMESTAMP
        }, error => {
          if (error) {
            reject(error);
          } else {
            resolve(data);
          }
        });
      });
    }).then(() => {
      return dispatch(
        sessionSetMissionReportAnswer({
          sessionId,
          level,
          questionNumber,
          question,
          answer,
          invalid
        })
      );
    });
  };
}

function sessionSetMode (sessionId, mode) {
  return {
    type: SESSION_SET_MODE,
    sessionId,
    mode
  };
}

export function setMode ({ sessionId, mode = null }) {
  return dispatch => {
    return new Promise((resolve, reject) => {
      ref.child('sessions').child(sessionId).update({
        mode
      }, error => {
        if (error) {
          reject(error);
        } else {
          resolve();
        }
      });
    }).then(() => {
      return dispatch(
        sessionSetMode(sessionId, mode)
      );
    });
  };
}

function sessionSetCurrentLevel (sessionId, currentLevel) {
  return {
    type: SESSION_SET_CURRENT_LEVEL,
    sessionId,
    currentLevel
  };
}

export function setCurrentLevel ({ sessionId, currentLevel }) {
  return dispatch => {
    return new Promise((resolve, reject) => {
      ref.child('sessions').child(sessionId).update({
        currentLevel: String(currentLevel)
      }, error => {
        if (error) {
          reject(error);
        } else {
          resolve();
        }
      });
    }).then(() => {
      return dispatch(
        sessionSetCurrentLevel(sessionId, String(currentLevel))
      );
    }).then(() => {
      return setCurrentSlide({ sessionId })(dispatch);
    }).then(() => {
      return setMode({ sessionId })(dispatch);
    });
  };
}

function sessionCompleteLevel ({ sessionId, programId, completedLevel, finished }) {
  return {
    type: SESSION_COMPLETE_LEVEL,
    sessionId,
    programId,
    completedLevel,
    finished
  };
}

export function completeLevel ({
  sessionId,
  completedLevel,
  programId,
  finished,
  contractId,
  userId,
  funding
}) {
  return dispatch => {
    const attributes = {
      completedLevel: Number(completedLevel),
      updatedAt: TIMESTAMP
    };
    if (finished) {
      attributes.completedAt = TIMESTAMP;
    }
    if (completedLevel === '1') {
      attributes.completedLevel1At = TIMESTAMP;
    }
    return ref.child('sessions').child(sessionId).update(attributes)
      .then(() => getSessionById(sessionId))
      .then(({language}) => {
        const levelName = program.programList.filter(p => p.id === programId)[0]["levels"][completedLevel]["levelName"];
        publishEvent('Level Completed', {
          userId,
          sessionId,
          programId,
          level: completedLevel,
          levelName,
          funding,
          contractId,
          language
        });
        if (finished) {
          publishEvent('Class Completed', {
            userId,
            sessionId,
            programId,
            funding,
            contractId,
            language
          });
        }
      }).then(() => {
        return dispatch(
          sessionCompleteLevel({
            sessionId,
            programId,
            completedLevel: Number(completedLevel),
            finished
          })
        )
      })
  }
}

export function advanceLevel({sessionId, completedLevel}) {
  return dispatch => {
    return Promise.resolve()
      .then(() => {
        return setCurrentLevel({
          sessionId,
          currentLevel: Number(completedLevel) + 1
        })(dispatch);
      }).then(() => {
        return setLastSlide({sessionId})(dispatch);
      });
  }
}

export function completeLevelAndAdvance ({
  sessionId,
  completedLevel,
  programId,
  finished,
  contractId,
  userId,
  funding
}) {
  return dispatch => {
    return Promise.resolve()
      .then(() => {
        return completeLevel({sessionId, completedLevel, programId, finished, contractId, userId, funding})(dispatch);
      }).then(() => {
        return advanceLevel({sessionId, completedLevel})(dispatch);
      })
  }
}

export function resetAddEditClassForm (data = {}) {
  return dispatch => {
    dispatch(initialize('addEditClass', data));
  };
}

function sessionSetConfiguration (sessionId, configuration) {
  return {
    type: SESSION_SET_CONFIGURATION,
    sessionId,
    configuration
  };
}

export function setConfiguration ({ sessionId, configuration = null }) {
  return dispatch => {
    return new Promise((resolve, reject) => {
      ref.child('sessions').child(sessionId).update({
        configuration
      }, error => {
        if (error) {
          reject(error);
        } else {
          resolve();
        }
      });
    }).then(() => {
      return dispatch(
        sessionSetConfiguration(sessionId, configuration)
      );
    });
  };
}

function sessionArchive (sessionId) {
  return {
    type: SESSION_ARCHIVE,
    sessionId
  };
}

export function archiveSession (sessionId, programId, userId) {
  return dispatch => {
    return new Promise((resolve, reject) => {
      ref.child('sessions').child(sessionId).update({
        archived: true,
        pendingApproval: null,
        updatedAt: TIMESTAMP,
        archivedAt: TIMESTAMP
      }, error => {
        if (error) {
          reject(error);
        } else {
          resolve();
        }
      });
    }).then(() => {
      return getSessionById(sessionId);
    }).then(({language}) => {
      publishEvent(`Class Archived`, {
        userId,
        sessionId,
        programId,
        language
      });
      return dispatch(
        sessionArchive(sessionId)
      );
    });
  };
}

function sessionSetHackedAnswer(sessionId, level, answer) {
    return {
        type: SESSION_SET_HACKED_ANSWER,
        sessionId,
        level,
        answer
    };
}

export function setHackedAnswer ({sessionId, level, answer}) {
    return dispatch => {
        return new Promise((resolve, reject) => {
            ref.child('sessions').child(sessionId).update({
                [`zh-hacked-answer-${level}`]: answer
            }, error => {
                if (error) {
                    reject(error);
                } else {
                    resolve();
                }
            });
        }).then(() => {
            return dispatch(
                sessionSetHackedAnswer(sessionId, answer)
            );
        });
    };
}



// WEBPACK FOOTER //
// ./src/actions/sessionActions.js